import { useState, useEffect, useRef, useMemo, useCallback } from "react"
import MapGL, { Popup, Source, Layer } from "react-map-gl/mapbox"
import "mapbox-gl/dist/mapbox-gl.css"
import type { MapRef, MapLayerMouseEvent } from "react-map-gl/mapbox"
import { bbox as turfBbox } from "@turf/turf"
import { CommunePluLayer } from "@/components/explore/layers/commune-plu-layer"
import { ProjectMapPopup } from "./project-map-popup"
import { fetchPluDocument, getPLUPartitionAreas } from "@/lib/explore/api/plu"
import { Switch } from "@/components/ui/switch"
import { Label } from "@/components/ui/label"
import type { Project, PipeStatusRecord } from "@/types"
import type { Feature, FeatureCollection } from "geojson"

const MAPBOX_STREET_STYLE = "mapbox://styles/kaptcher/cm1umwib0015k01qr8tra14o9"

const CADASTRE_VECTOR_SOURCE = "https://openmaptiles.geo.data.gouv.fr/data/cadastre/{z}/{x}/{y}.pbf"

type Props = {
  projects: Project[]
  mapboxToken: string
  pipeStatuses: PipeStatusRecord[]
}

function createMarkerSvg(bgColor: string, textColor: string): string {
  // Lucide MapPin icon paths (viewBox 0 0 24 24)
  return `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="${bgColor}" stroke="${textColor}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
    <path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0"/>
    <circle cx="12" cy="10" r="3"/>
  </svg>`
}

function svgToImage(svg: string, size: number): Promise<ImageData> {
  return new Promise((resolve) => {
    const img = new Image(size, size)
    img.onload = () => {
      const canvas = document.createElement("canvas")
      canvas.width = size
      canvas.height = size
      const ctx = canvas.getContext("2d")!
      ctx.drawImage(img, 0, 0, size, size)
      resolve(ctx.getImageData(0, 0, size, size))
    }
    img.src = "data:image/svg+xml;charset=utf-8," + encodeURIComponent(svg)
  })
}

export function ProjectsMapView({ projects, mapboxToken, pipeStatuses }: Props) {
  const mapRef = useRef<MapRef>(null)
  const [selectedProject, setSelectedProject] = useState<Project | null>(null)
  const [showCadastre, setShowCadastre] = useState(false)
  const [showPlu, setShowPlu] = useState(false)
  const [pluData, setPluData] = useState<Feature[]>([])
  const [pluPartition, setPluPartition] = useState<string | null>(null)
  const [markersReady, setMarkersReady] = useState(false)
  const [currentZoom, setCurrentZoom] = useState(5.5)
  const hasFitted = useRef(false)

  // Build GeoJSON from projects
  const geojson = useMemo<FeatureCollection>(() => {
    const features = projects
      .filter((p) => p.latitude && p.longitude)
      .map((p) => ({
        type: "Feature" as const,
        geometry: {
          type: "Point" as const,
          coordinates: [p.longitude!, p.latitude!],
        },
        properties: {
          id: p.id,
          bg_color: p.pipe_status?.bg_color || "#6366f1",
          icon_id: `marker-${(p.pipe_status?.bg_color || "#6366f1").replace("#", "")}-${(p.pipe_status?.text_color || "#ffffff").replace("#", "")}`,
        },
      }))
    return { type: "FeatureCollection", features }
  }, [projects])

  // Register marker images for each unique color
  const registerMarkers = useCallback(async () => {
    const map = mapRef.current?.getMap()
    if (!map) return

    const colorPairs = new Map<string, { bg: string; text: string }>()
    colorPairs.set("6366f1-ffffff", { bg: "#6366f1", text: "#ffffff" }) // fallback
    pipeStatuses.forEach((ps) => {
      const key = `${ps.bg_color.replace("#", "")}-${ps.text_color.replace("#", "")}`
      colorPairs.set(key, { bg: ps.bg_color, text: ps.text_color })
    })

    const MARKER_SIZE = 56 // 2x for retina
    for (const [key, { bg, text }] of colorPairs) {
      const imageId = `marker-${key}`
      if (map.hasImage(imageId)) continue
      const svg = createMarkerSvg(bg, text)
      const imageData = await svgToImage(svg, MARKER_SIZE)
      if (!map.hasImage(imageId)) {
        map.addImage(imageId, imageData, { pixelRatio: 2 })
      }
    }
    setMarkersReady(true)
  }, [pipeStatuses])

  // Auto-fit bounds on mount
  useEffect(() => {
    if (hasFitted.current || geojson.features.length === 0) return
    const map = mapRef.current
    if (!map) return

    try {
      const [minLng, minLat, maxLng, maxLat] = turfBbox(geojson)
      map.fitBounds(
        [
          [minLng, minLat],
          [maxLng, maxLat],
        ],
        { padding: 60, maxZoom: 15, duration: 0 },
      )
      hasFitted.current = true
    } catch {
      // fallback: center on France
    }
  }, [geojson])

  // Fetch PLU data based on map center coordinates
  const fetchPluAtCenter = useCallback(async () => {
    const map = mapRef.current
    if (!map) return
    const zoom = map.getZoom()
    if (zoom < 10) {
      if (pluPartition) {
        setPluPartition(null)
        setPluData([])
      }
      return
    }

    const center = map.getCenter()
    const doc = await fetchPluDocument(center.lng, center.lat)
    if (!doc?.partition || doc.partition === pluPartition) return

    setPluPartition(doc.partition)
    try {
      const pluAreas = await getPLUPartitionAreas(doc.partition)
      setPluData(pluAreas.features as Feature[])
    } catch (error) {
      console.error("Error fetching PLU data:", error)
      setPluData([])
    }
  }, [pluPartition])

  const handleMoveEnd = useCallback(() => {
    const map = mapRef.current
    if (map) setCurrentZoom(map.getZoom())
    if (showPlu) fetchPluAtCenter()
  }, [showPlu, fetchPluAtCenter])

  // Trigger PLU fetch when toggling on
  useEffect(() => {
    if (showPlu) fetchPluAtCenter()
  }, [showPlu])

  const handleClick = useCallback(
    (e: MapLayerMouseEvent) => {
      const feature = e.features?.[0]
      if (!feature) {
        setSelectedProject(null)
        return
      }
      const project = projects.find((p) => p.id === feature.properties?.id)
      if (project) setSelectedProject(project)
    },
    [projects],
  )

  return (
    <div className="relative flex-1 overflow-hidden rounded-lg border [&_.mapboxgl-ctrl-logo]:!hidden">
      <MapGL
        ref={mapRef}
        mapboxAccessToken={mapboxToken}
        mapStyle={MAPBOX_STREET_STYLE}
        initialViewState={{
          longitude: 2.3522,
          latitude: 46.6034,
          zoom: 5.5,
        }}
        interactiveLayerIds={["projects-fill", "projects-marker"]}
        onClick={handleClick}
        onMoveEnd={handleMoveEnd}
        onLoad={registerMarkers}
        style={{ width: "100%", height: "100%" }}
        attributionControl={false}
        cursor="pointer"
      >
        {/* Cadastre vector tiles */}
        {showCadastre && (
          <Source
            id="cadastre-vector"
            type="vector"
            tiles={[CADASTRE_VECTOR_SOURCE]}
            minzoom={11}
            maxzoom={16}
          >
            <Layer
              id="cadastre-parcels-line"
              source-layer="parcelles"
              type="line"
              paint={{
                "line-color": "#000000",
                "line-width": 1,
                "line-opacity": 0.5,
              }}
            />
            <Layer
              id="cadastre-parcels-label"
              source-layer="parcelles"
              type="symbol"
              minzoom={16}
              filter={["!=", ["get", "section"], ""]}
              layout={{
                "text-field": ["concat", ["get", "section"], " ", ["get", "numero"]],
                "text-size": 14,
                "text-allow-overlap": false,
              }}
              paint={{
                "text-color": "#000000",
                "text-halo-color": "#ffffff",
                "text-halo-width": 1.5,
              }}
            />
          </Source>
        )}

        {/* PLU layer */}
        {showPlu && pluData.length > 0 && (
          <CommunePluLayer
            pluTypeZones={["U", "AUc", "AUs", "N", "A"]}
            pluData={pluData}
            opacity={0.4}
          />
        )}

        {/* Project markers — same style as explorer */}
        <Source id="projects-source" type="geojson" data={geojson}>
          <Layer
            id="projects-fill"
            type="fill"
            filter={["!=", ["geometry-type"], "Point"]}
            paint={{
              "fill-color": ["get", "bg_color"],
              "fill-opacity": 0.35,
            }}
          />
          <Layer
            id="projects-outline"
            type="line"
            filter={["!=", ["geometry-type"], "Point"]}
            paint={{
              "line-color": ["get", "bg_color"],
              "line-width": 2,
            }}
          />
          {markersReady && (
            <Layer
              id="projects-marker"
              type="symbol"
              filter={["==", ["geometry-type"], "Point"]}
              layout={{
                "icon-image": ["get", "icon_id"],
                "icon-size": ["interpolate", ["linear"], ["zoom"], 5, 0.6, 12, 0.9, 16, 1.1],
                "icon-anchor": "bottom",
                "icon-allow-overlap": true,
              }}
            />
          )}
        </Source>

        {/* Project popup */}
        {selectedProject && selectedProject.latitude && selectedProject.longitude && (
          <Popup
            longitude={selectedProject.longitude}
            latitude={selectedProject.latitude}
            onClose={() => setSelectedProject(null)}
            closeButton={false}
            closeOnClick={false}
            anchor="bottom"
            offset={24}
            maxWidth="320px"
          >
            <ProjectMapPopup project={selectedProject} onClose={() => setSelectedProject(null)} />
          </Popup>
        )}
      </MapGL>

      {/* Layer toggles */}
      <div className="absolute top-3 right-3 flex flex-col gap-2 rounded-lg border bg-background/90 p-3 shadow-sm backdrop-blur-sm">
        <div className="flex items-center gap-2">
          <Switch id="cadastre-toggle" checked={showCadastre} onCheckedChange={setShowCadastre} />
          <Label htmlFor="cadastre-toggle" className="text-xs cursor-pointer">Cadastre</Label>
        </div>
        <div className="flex items-center gap-2">
          <Switch id="plu-toggle" checked={showPlu} onCheckedChange={setShowPlu} disabled={currentZoom < 10} />
          <Label htmlFor="plu-toggle" className={`text-xs ${currentZoom < 10 ? "text-muted-foreground/50" : "cursor-pointer"}`}>
            PLU{currentZoom < 10 ? " (zoomer +)" : ""}
          </Label>
        </div>
      </div>
    </div>
  )
}
